import type { SkillDef } from "../types";
import { digits, fmt, prob } from "../util";

type Conv = { from: string; to: string; fn: (x: number) => number; rule: string; beginner: boolean };

/** Add a conversion here and it joins the rotation. beginner: true makes it appear at levels 1–3. */
export const CONVERSIONS: Conv[] = [
  { from: "km", to: "mi", fn: (x) => x * 0.621371, rule: "×5/8 (×0.6 runs about 3% low)", beginner: true },
  { from: "mi", to: "km", fn: (x) => x * 1.609344, rule: "×1.6 (or ×8/5)", beginner: true },
  { from: "kg", to: "lb", fn: (x) => x * 2.204623, rule: "double it, then add 10%", beginner: true },
  { from: "lb", to: "kg", fn: (x) => x * 0.453592, rule: "halve it, then subtract 10%", beginner: true },
  { from: "°C", to: "°F", fn: (x) => x * 1.8 + 32, rule: "double, subtract 10%, add 32", beginner: true },
  { from: "°F", to: "°C", fn: (x) => (x - 32) / 1.8, rule: "subtract 32, halve, add 10%", beginner: false },
  { from: "in", to: "cm", fn: (x) => x * 2.54, rule: "×2.5, a hair more", beginner: false },
  { from: "cm", to: "in", fn: (x) => x * 0.393701, rule: "×0.4, a hair less", beginner: false },
  { from: "m", to: "ft", fn: (x) => x * 3.28084, rule: "×3, then add 10%", beginner: false },
  { from: "ft", to: "m", fn: (x) => x * 0.3048, rule: "×0.3, a hair more", beginner: false },
  { from: "L", to: "gal", fn: (x) => x * 0.264172, rule: "÷4, a bit more", beginner: false },
  { from: "gal", to: "L", fn: (x) => x * 3.785412, rule: "×4, then subtract 5%", beginner: false },
  { from: "oz", to: "g", fn: (x) => x * 28.3495, rule: "×30, then subtract 5%", beginner: false },
  { from: "km/h", to: "mph", fn: (x) => x * 0.621371, rule: "×5/8 (×0.6 runs about 3% low)", beginner: false },
];

export const unit: SkillDef = {
  key: "unit", name: "Unit conversion", blurb: "rules of thumb", start: 1,
  generate(L, r) {
    const pool = L <= 3 ? CONVERSIONS.filter((c) => c.beginner) : CONVERSIONS;
    const c = r.pick(pool);
    let x: number, tol: number, rel: boolean;
    if (c.from.startsWith("°")) {
      x = c.from === "°C" ? (L === 1 ? r.int(-1, 4) * 10 : L === 2 ? r.int(-4, 9) * 5 : r.int(-20, 45)) : r.int(0, 110);
      tol = L <= 3 ? 3 : L <= 6 ? 2 : 1.5;
      rel = false;
    } else {
      x = L === 1 ? r.int(1, 10) * 10 : L === 2 ? r.int(1, 40) * 5 : L <= 6 ? r.int(11, 99) : L <= 8 ? r.int(101, 999) : Math.round((10 + r.float() * 490) * 10) / 10;
      tol = L <= 3 ? 0.05 : L <= 6 ? 0.03 : 0.02;
      rel = true;
    }
    const val = Math.round(c.fn(x) * 100) / 100;
    return prob(`${fmt(x)} ${c.from} → ${c.to}`, val, `Rule of thumb: ${c.rule}.\nExact ≈ ${fmt(val)}`, {
      tol, rel,
      note: rel ? `within ±${Math.round(tol * 100)}%` : `within ±${fmt(tol)}°`,
      t: 5 + 0.8 * digits(x) + (Number.isInteger(x) ? 0 : 2) + (rel ? 0 : 2) + (tol <= 0.02 ? 1.5 : 0),
    });
  },
};
